import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import type { SaveFeedback } from './useModuleConfigEditor';

/**
 * Save region of the Module Configuration page (CO-864, CO-863): the outcome of the last save, and,
 * only while there are staged changes, a bar that says how many, for which tenant, and lists them
 * before they are applied.
 *
 * A failed save says which of three things happened, and none of the three removes itself (CO-860,
 * CO-863): the server refused and nothing was written (its own reason is shown); the configuration
 * changed elsewhere since this screen read it (Reload keeps the staged changes); or no usable answer
 * came back, so the screen does not claim either outcome and offers the same Reload.
 *
 * Page-local on purpose. The shared `StickyActionBar` is always mounted, has no place for a count or
 * a list, and is used by every other save page; it is left untouched.
 *
 * The bar has one action row. Closed, it offers Discard and Review. Open, the list appears above the
 * row and the row becomes Back and Apply, so Apply is reachable only with the list on screen. The
 * list is a bounded scroller (a deliberate one) and the whole bar is marked `data-fab-obstacle` so
 * the assistant launcher lifts clear of it; the list's height is capped to keep the bar under the
 * launcher's 40 percent lift limit (`components/ai-chat/AIAssistant.tsx`).
 */

export type ChangeItemKind = 'module' | 'page' | 'tab' | 'capability' | 'mobilePage';

/** One staged change, ready to display. */
export interface ChangeItem {
  key: string;
  kind: ChangeItemKind;
  /** Translated name of the switch. */
  label: string;
  /** Translated name of the module, tab group or set the switch sits in. Omitted for a module itself. */
  owner?: string;
  from: boolean;
  to: boolean;
  /** A whole module going from on to off: every page under it disappears for the tenant. */
  turnsModuleOff: boolean;
  /** A demo surface going from off to on. Set once the server names the demo keys (CO-861). */
  turnsDemoOn?: boolean;
}

interface ChangeBarProps {
  companyName: string;
  items: ChangeItem[];
  saving: boolean;
  /** A reload that keeps the staged changes is in flight. */
  reloading: boolean;
  feedback: SaveFeedback;
  reviewOpen: boolean;
  onReviewOpenChange: (open: boolean) => void;
  onDiscard: () => void;
  onApply: () => void;
  /** Read the company's configuration again, keeping the staged changes on top of it. */
  onReload: () => void;
  onDismissFeedback: () => void;
}

function ChangeLines({ heading, items }: { heading: string; items: ChangeItem[] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <div className="pt-2">
      <h3 className="pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{heading}</h3>
      <ul>
        {items.map(item => (
          <li
            key={`${item.kind}:${item.key}`}
            data-testid={`module-config-change-${item.key}`}
            className="flex items-baseline justify-between gap-3 py-1 text-sm"
          >
            <span className="min-w-0">
              <span className="text-foreground">{item.label}</span>
              <span className="ms-2 text-xs text-muted-foreground">
                {item.owner
                  ? t('moduleConfig.review.kindIn', { kind: t(`moduleConfig.kind.${item.kind}`), owner: item.owner })
                  : t(`moduleConfig.kind.${item.kind}`)}
              </span>
            </span>
            <span className={`shrink-0 text-xs font-medium ${item.to ? 'text-success-strong' : 'text-warning-strong'}`}>
              {item.to ? t('moduleConfig.review.offToOn') : t('moduleConfig.review.onToOff')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ChangeBar({
  companyName,
  items,
  saving,
  reloading,
  feedback,
  reviewOpen,
  onReviewOpenChange,
  onDiscard,
  onApply,
  onReload,
  onDismissFeedback,
}: ChangeBarProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const titleId = useId();

  const count = items.length;
  const turnedOn = items.filter(item => item.to).length;
  const demoOn = items.filter(item => item.turnsDemoOn);
  const modulesOff = items.filter(item => item.turnsModuleOff && !item.turnsDemoOn);
  const other = items.filter(item => !item.turnsModuleOff && !item.turnsDemoOn);

  const busy = saving || reloading;
  // The version this screen holds is known to be stale, so Apply cannot succeed until a reload.
  const conflict = feedback?.type === 'conflict';

  let errorText = '';
  if (feedback?.type === 'error') {
    if (feedback.outcomeUnknown) errorText = t('moduleConfig.saveUnknown');
    else if (feedback.reason) errorText = t('moduleConfig.saveFailedWithReason', { reason: feedback.reason });
    else errorText = t('moduleConfig.saveFailed');
  }

  const reloadButton = (
    <Button
      variant="outline"
      size="sm"
      className="shrink-0"
      loading={reloading}
      disabled={busy}
      onClick={onReload}
      data-testid="module-config-reload"
    >
      {t('moduleConfig.reload')}
    </Button>
  );

  return (
    <div className="sticky bottom-0 z-10 flex flex-col" data-testid="module-config-save-region">
      {/* Rendered empty before the save so the live region exists when its text arrives. */}
      <div
        role="status"
        aria-live="polite"
        data-testid="module-config-save-status"
        className={feedback?.type === 'success' ? 'mt-6 rounded-lg bg-card shadow-lg' : undefined}
      >
        {feedback?.type === 'success' && (
          <div className="flex items-center gap-3 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success-strong">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{t('Configuration saved successfully')}</span>
          </div>
        )}
      </div>

      {/* Kept until it is dismissed or the next save starts; the staged changes stay staged. The
          reason is the server's own sentence and can be long, so it wraps and the actions drop below
          it on a phone. */}
      {feedback?.type === 'error' && (
        <div role="alert" data-testid="module-config-save-error" className="mt-6 rounded-lg bg-card shadow-lg">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 basis-48 break-words">{errorText}</span>
            {feedback.outcomeUnknown && reloadButton}
            <button
              type="button"
              onClick={onDismissFeedback}
              aria-label={t('Dismiss')}
              className="-m-2 shrink-0 rounded-lg p-2 hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Not dismissible: it goes when the reload succeeds, and Apply stays off until then. */}
      {feedback?.type === 'conflict' && (
        <div role="alert" data-testid="module-config-save-conflict" className="mt-6 rounded-lg bg-card shadow-lg">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-foreground">
            <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0 text-warning-strong" />
            <span className="min-w-0 flex-1 basis-48">
              {t('moduleConfig.conflict')}
              {feedback.reloadFailed && (
                <span className="mt-1 block font-medium text-destructive" data-testid="module-config-reload-failed">
                  {t('moduleConfig.reloadFailed')}
                </span>
              )}
            </span>
            {reloadButton}
          </div>
        </div>
      )}

      {count > 0 && (
        <div
          data-testid="module-config-change-bar"
          data-fab-obstacle
          className="mt-3 rounded-xl border border-border bg-card shadow-lg"
        >
          {reviewOpen && (
            <div id={panelId} role="region" aria-labelledby={titleId} data-testid="module-config-review-panel">
              <h2 id={titleId} className="px-4 pt-3 text-sm font-semibold text-foreground sm:px-6">
                {t('moduleConfig.review.title')}
              </h2>
              {/* Bounded on purpose: a long list scrolls here, never the document. Focusable so the
                  keyboard can scroll it. */}
              <div
                tabIndex={0}
                className="max-h-[min(12rem,22vh)] overflow-y-auto border-b border-border px-4 pb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-6"
              >
                <ChangeLines heading={t('moduleConfig.review.demoOn')} items={demoOn} />
                <ChangeLines heading={t('moduleConfig.review.modulesOff')} items={modulesOff} />
                <ChangeLines heading={t('moduleConfig.review.other')} items={other} />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:px-6">
            <p className="min-w-0 text-sm" data-testid="module-config-change-summary" data-count={count}>
              <span className="font-medium text-foreground">
                {t('moduleConfig.changes.summary', { count, company: companyName })}
              </span>{' '}
              <span className="text-muted-foreground">
                {t('moduleConfig.changes.onOff', { on: turnedOn, off: count - turnedOn })}
              </span>
            </p>

            <div className="flex items-center gap-2 sm:ms-auto">
              {reviewOpen ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    disabled={busy}
                    onClick={() => onReviewOpenChange(false)}
                    data-testid="module-config-review-back"
                  >
                    {t('moduleConfig.review.back')}
                  </Button>
                  <Button
                    size="sm"
                    className="min-w-0 flex-1 sm:flex-none"
                    loading={saving}
                    disabled={busy || conflict}
                    onClick={onApply}
                    data-testid="module-config-apply"
                  >
                    <span className="truncate">{t('moduleConfig.review.apply', { count, company: companyName })}</span>
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    disabled={busy}
                    onClick={onDiscard}
                    data-testid="module-config-discard"
                  >
                    {t('moduleConfig.unsaved.discard')}
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 sm:flex-none"
                    disabled={busy}
                    aria-expanded={false}
                    onClick={() => onReviewOpenChange(true)}
                    data-testid="module-config-review"
                  >
                    {t('moduleConfig.review.open')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface UnsavedChangesDialogProps {
  open: boolean;
  /** The tenant that holds the staged changes. */
  companyName: string;
  /** Keep the tenant and the staged changes. Also what Escape and a click outside do. */
  onStay: () => void;
  /** Drop the staged changes and go where the operator asked. */
  onDiscard: () => void;
  /** Keep the tenant and open the review, which is the only way to save. */
  onReview: () => void;
}

/**
 * The one blocking prompt of the page: leaving a tenant with staged changes. A dialog is the right
 * tool here because the navigation it interrupts would destroy work; nothing else on the page opens
 * one.
 */
export function UnsavedChangesDialog({ open, companyName, onStay, onDiscard, onReview }: UnsavedChangesDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={next => { if (!next) onStay(); }}>
      <DialogContent size="sm" hideCloseButton data-testid="module-config-unsaved-dialog">
        <DialogHeader>
          <DialogTitle>{t('moduleConfig.unsaved.title', { company: companyName })}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>{t('moduleConfig.unsaved.body')}</DialogDescription>
        </DialogBody>
        <DialogFooter className="flex-wrap">
          <Button variant="outline" onClick={onStay} data-testid="module-config-unsaved-stay">
            {t('moduleConfig.unsaved.stay')}
          </Button>
          <Button variant="destructive" onClick={onDiscard} data-testid="module-config-unsaved-discard">
            {t('moduleConfig.unsaved.discard')}
          </Button>
          <Button onClick={onReview} data-testid="module-config-unsaved-review">
            {t('moduleConfig.unsaved.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
